/**
 * ModeToggle — presents Voice and Text mode selection buttons.
 * Shown only when the session has not yet started (mode === 'idle' / before mode selected).
 *
 * Phase 9: SuggestionBanner added above mode buttons (D-12).
 * onFocusAreaId? optional callback — called when user accepts/picks a suggestion area.
 * onPhotoId? optional callback — called when user uploads (or removes) a session photo.
 */

import { useRef, useState } from 'react';
import { StoryPicker } from '../StoryPicker/StoryPicker';
import { requestUploadUrl, uploadPhotoToS3, pollPhotoReady } from '../../lib/photoUpload';

interface ModeToggleProps {
  onSelect: (mode: 'voice' | 'text') => void;
  onFocusAreaId?: (areaId: string | null, topicId?: string, topicTitle?: string) => void;
  onPhotoId?: (photoId: string | null) => void;
}

export function ModeToggle({ onSelect, onFocusAreaId, onPhotoId }: ModeToggleProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [_uploadedPhotoId, setUploadedPhotoId] = useState<string | null>(null);
  const [topicSelected, setTopicSelected] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleAccept(areaId: string, topicId?: string, topicTitle?: string) {
    onFocusAreaId?.(areaId, topicId, topicTitle);
    setTopicSelected(true);
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    setUploading(true);
    setUploadError(null);

    try {
      const { uploadUrl, photoId } = await requestUploadUrl(file.name, file.type);
      await uploadPhotoToS3(uploadUrl, file);
      await pollPhotoReady(photoId);
      setUploadedPhotoId(photoId);
      onPhotoId?.(photoId);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed — please try again');
      URL.revokeObjectURL(objectUrl);
      setPreviewUrl(null);
      setUploadedPhotoId(null);
      onPhotoId?.(null);
    } finally {
      setUploading(false);
    }
  }

  function handleRemovePhoto() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setUploadedPhotoId(null);
    setUploadError(null);
    onPhotoId?.(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  return (
    <div className="flex flex-col items-center gap-6 py-8 w-full">
      {/* Story picker — mandatory before mode selection (PARITY-04) */}
      <div className="w-full max-w-md">
        <StoryPicker onAccept={handleAccept} />
      </div>

      {/* Optional photo upload */}
      <div className="w-full max-w-md">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => void handleFileChange(e)}
          aria-label="Upload a photo"
        />
        {previewUrl ? (
          <div className="flex items-center gap-3 p-3 bg-stone-50 border border-stone-200 rounded-xl">
            <img src={previewUrl} alt="Session photo preview" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
            <div className="flex-1 min-w-0">
              {uploading ? (
                <p className="text-sm text-stone-500">Uploading photo…</p>
              ) : uploadError ? (
                <p className="text-sm text-red-600">{uploadError}</p>
              ) : (
                <p className="text-sm text-stone-700 font-medium">Photo added</p>
              )}
            </div>
            <button
              type="button"
              onClick={handleRemovePhoto}
              disabled={uploading}
              className="text-stone-400 hover:text-red-500 transition-colors text-xl leading-none disabled:opacity-40 px-1"
              aria-label="Remove photo"
            >
              ×
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-stone-300 text-stone-500 hover:border-amber-400 hover:text-amber-600 hover:bg-amber-50 transition-all text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 flex-shrink-0" aria-hidden="true">
              <path fillRule="evenodd" d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z" clipRule="evenodd" />
            </svg>
            Add a photo to help tell your story (optional)
          </button>
        )}
        {uploadError && !previewUrl && (
          <p className="text-xs text-red-600 mt-1.5 text-center">{uploadError}</p>
        )}
      </div>

      {topicSelected ? (
        <>
          <h2 className="text-xl font-semibold text-stone-800">How would you like to talk today?</h2>
          <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
            <button
              type="button"
              onClick={() => onSelect('voice')}
              disabled={uploading}
              className="flex-1 flex flex-col items-center gap-3 p-6 rounded-2xl border-2 border-stone-200 bg-white hover:border-amber-400 hover:bg-amber-50 transition-all focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Select voice mode"
            >
              <span className="text-4xl" role="img" aria-hidden="true">🎙️</span>
              <span className="text-lg font-semibold text-stone-800">Speak (Voice)</span>
              <span className="text-sm text-stone-500 text-center">Talk out loud — AI will listen and respond with audio</span>
            </button>

            <button
              type="button"
              onClick={() => onSelect('text')}
              disabled={uploading}
              className="flex-1 flex flex-col items-center gap-3 p-6 rounded-2xl border-2 border-stone-200 bg-white hover:border-sky-400 hover:bg-sky-50 transition-all focus:outline-none focus:ring-2 focus:ring-sky-400 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Select text mode"
            >
              <span className="text-4xl" role="img" aria-hidden="true">⌨️</span>
              <span className="text-lg font-semibold text-stone-800">Type (Text)</span>
              <span className="text-sm text-stone-500 text-center">Type your answers — great for quiet environments</span>
            </button>
          </div>
          {uploading && (
            <p className="text-xs text-stone-400">Please wait while your photo uploads…</p>
          )}
        </>
      ) : (
        <p className="text-sm text-stone-400 text-center">Select a story above to start your session</p>
      )}
    </div>
  );
}
